import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createPatientTrackingDiscussionMessage,
  deletePatientTrackingDiscussionMessage,
  fetchPatientTrackingDiscussionThread,
  updatePatientTrackingDiscussionMessage
} from './patientTrackingDiscussionApi';
import { PATIENT_TRACKING_API_BASE } from '../patientTrackingTypes';
import { createPatientTrackingDiscussionClient } from './createPatientTrackingDiscussionClient';
import { discoverPatientTrackingPeerActivity } from './patientTrackingDiscussionPeerActivity';
import type {
  PatientTrackingDiscussionChangedNotice,
  PatientTrackingDiscussionTargetType,
  PatientTrackingDiscussionThreadDto
} from './patientTrackingDiscussionTypes';

const POLL_INTERVAL_MS = 60_000;
const REALTIME_RELOAD_DEBOUNCE_MS = 250;
const EMPTY_MESSAGES: ReadonlyArray<import('./patientTrackingDiscussionTypes').PatientTrackingDiscussionMessage> = [];

function threadContentEqual(
  current: PatientTrackingDiscussionThreadDto | null,
  next: PatientTrackingDiscussionThreadDto
): boolean {
  if (!current) return false;
  if (current.threadId !== next.threadId) return false;
  if (current.messages.length !== next.messages.length) return false;
  for (let i = 0; i < current.messages.length; i++) {
    const a = current.messages[i];
    const b = next.messages[i];
    if (a.id !== b.id || a.updatedAt !== b.updatedAt || a.text !== b.text) return false;
  }
  return true;
}

export interface UsePatientTrackingDiscussionOptions {
  panelOpen?: boolean;
  recordLabel?: string;
  apiBase?: string;
  onError?: (message: string) => void;
  currentUserId?: string;
  currentUserAliases?: Iterable<string>;
  onPeerActivity?: (event: import('@jarvis/platform-file-review').DiscussionPeerActivityEvent) => void;
}

export function usePatientTrackingDiscussion(
  targetType: PatientTrackingDiscussionTargetType,
  targetId: string | null,
  enabled: boolean,
  options?: UsePatientTrackingDiscussionOptions
) {
  const panelOpen = options?.panelOpen ?? true;
  const recordLabel = options?.recordLabel ?? 'this record';
  const apiBase = options?.apiBase ?? PATIENT_TRACKING_API_BASE;
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;
  const onPeerActivityRef = useRef(options?.onPeerActivity);
  onPeerActivityRef.current = options?.onPeerActivity;
  const currentUserIdRef = useRef(options?.currentUserId ?? 'unknown');
  currentUserIdRef.current = options?.currentUserId ?? 'unknown';
  const currentUserAliasesRef = useRef(options?.currentUserAliases ?? []);
  currentUserAliasesRef.current = options?.currentUserAliases ?? [];

  const [thread, setThread] = useState<PatientTrackingDiscussionThreadDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const threadRef = useRef<PatientTrackingDiscussionThreadDto | null>(null);
  const reloadSeqRef = useRef(0);
  const pendingNoticesRef = useRef<PatientTrackingDiscussionChangedNotice[]>([]);

  const notifyError = useCallback((e: unknown) => {
    const message = e instanceof Error ? e.message : String(e);
    setError(message);
    onErrorRef.current?.(message);
    return message;
  }, []);

  useEffect(() => {
    threadRef.current = null;
    pendingNoticesRef.current = [];
    setThread(null);
    setError(null);
    setLoading(false);
    reloadSeqRef.current += 1;
  }, [targetType, targetId]);

  const emitPeerActivityIfNeeded = useCallback(
    (previousThread: PatientTrackingDiscussionThreadDto | null, nextThread: PatientTrackingDiscussionThreadDto, silent: boolean) => {
      if (!silent || !onPeerActivityRef.current) return;
      const notices = pendingNoticesRef.current;
      pendingNoticesRef.current = [];
      const event = discoverPatientTrackingPeerActivity(
        previousThread,
        nextThread,
        notices,
        currentUserIdRef.current,
        currentUserAliasesRef.current,
        recordLabel
      );
      if (event) onPeerActivityRef.current(event);
    },
    [recordLabel]
  );

  const loadThread = useCallback(
    async (silent: boolean) => {
      if (!targetId || !enabled) return;
      const seq = ++reloadSeqRef.current;
      if (!silent) setLoading(true);
      try {
        const next = await fetchPatientTrackingDiscussionThread(targetType, targetId, apiBase);
        if (seq !== reloadSeqRef.current) return;
        const previous = threadRef.current;
        if (!threadContentEqual(previous, next)) {
          emitPeerActivityIfNeeded(previous, next, silent);
          threadRef.current = next;
          setThread(next);
        }
        setError(null);
      } catch (e) {
        if (seq === reloadSeqRef.current) notifyError(e);
      } finally {
        if (seq === reloadSeqRef.current && !silent) setLoading(false);
      }
    },
    [apiBase, emitPeerActivityIfNeeded, enabled, notifyError, targetId, targetType]
  );

  const loadThreadRef = useRef(loadThread);
  loadThreadRef.current = loadThread;

  useEffect(() => {
    if (!enabled || !targetId) return;
    void loadThread(false);
  }, [enabled, loadThread, targetId]);

  useEffect(() => {
    if (!enabled || !targetId || !panelOpen) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReload = (notice: PatientTrackingDiscussionChangedNotice) => {
      pendingNoticesRef.current.push(notice);
      if (debounceTimer != null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void loadThreadRef.current(true);
      }, REALTIME_RELOAD_DEBOUNCE_MS);
    };

    const client = createPatientTrackingDiscussionClient({
      targetType,
      targetId,
      onDiscussionChanged: scheduleReload
    });
    void client.start();

    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadThreadRef.current(true);
    }, POLL_INTERVAL_MS);

    return () => {
      if (debounceTimer != null) clearTimeout(debounceTimer);
      window.clearInterval(poll);
      void client.stop();
    };
  }, [enabled, panelOpen, targetId, targetType]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!targetId) return;
      await createPatientTrackingDiscussionMessage(targetType, targetId, text, apiBase);
      await loadThread(true);
    },
    [apiBase, loadThread, targetId, targetType]
  );

  const editMessage = useCallback(
    async (messageId: string, text: string) => {
      if (!targetId) return;
      await updatePatientTrackingDiscussionMessage(targetType, targetId, messageId, text, apiBase);
      await loadThread(true);
    },
    [apiBase, loadThread, targetId, targetType]
  );

  const removeMessage = useCallback(
    async (messageId: string) => {
      if (!targetId) return;
      await deletePatientTrackingDiscussionMessage(targetType, targetId, messageId, apiBase);
      await loadThread(true);
    },
    [apiBase, loadThread, targetId, targetType]
  );

  return {
    thread,
    messages: thread?.messages ?? (EMPTY_MESSAGES as PatientTrackingDiscussionThreadDto['messages']),
    loading,
    error,
    sendMessage,
    editMessage,
    removeMessage,
    reload: () => loadThread(false)
  };
}
